import { createLogger } from "@/logging";
import type { AuthSession } from "@/modules/auth/public";
import { annotationFrameStores } from "@/page/annotationStudio/data/AnnotationFrameBytesCache";
import { IdbCache, type IdbDatabaseSchema } from "@/shared/storage/idb/public";
import type { AppSessionEndReason } from "./AppSessionLifecycle";

/**
 * 앱의 브라우저 캐시 데이터베이스.
 *
 * Owns:
 * - 스토어 목록과 스키마 버전. 소비처가 선언한 스토어를 모아 조립한다.
 * - 세션이 끝나는 사유별 폐기 정책.
 * - 실패를 기록할 위치. 커널은 로거를 고르지 않는다.
 *
 * Does not own:
 * - 트랜잭션·연결·degrade(`shared/storage/idb`).
 * - 레코드의 뜻(각 소비처).
 *
 * 데이터베이스는 하나다. 트랜잭션은 DB 경계를 넘지 못한다. 소비처별로 DB 를 나누면
 * "쓰고 장부를 맞추는" 작업을 한 트랜잭션으로 만들 수 없다. 또 쓰기 대기열과 세션 폐기 훅이
 * 소비처 수만큼 늘어난다.
 */

const logger = createLogger("app/hatcheryDatabase");

const schema: IdbDatabaseSchema = {
  // `hatchery-registry:` 접두는 localStorage 와 같다. `-cache` 접미는 "버려도 되는 데이터" 임을
  // 이름에 표시한다. 스토어 집합이나 keyPath 가 바뀌면 version 을 올린다. 마이그레이션은 하지
  // 않고, 형태가 바뀐 스토어는 비운다. 캐시를 옮기는 올바른 방법은 폐기다.
  name: "hatchery-registry-cache",
  version: 1,
  stores: [...annotationFrameStores],
};

export const hatcheryDatabase = new IdbCache({
  schema,
  // 미지원 환경(구형 브라우저·일부 사설 모드·테스트의 node 환경)에서는 값이 null 이고 캐시 전체가
  // 꺼진다. 기능은 캐시 없이 그대로 동작한다.
  factory: typeof indexedDB === "undefined" ? null : indexedDB,
  onFailure: (message, error) => logger.warn(message, error),
});

/**
 * 현재 세션의 사용자다. 캐시 레코드를 사용자별로 구분하는 키의 한 부분이다.
 *
 * 앱 상태(React context)가 아니라 여기서 읽는다. 이 값은 데이터베이스 수명을 정하는 사건
 * (세션 시작·종료)에서만 바뀐다. 두 값을 같은 곳에 두어야 서로 어긋나지 않는다.
 * 소비처가 context 를 쓰면, provider 밖에서 쓰는 훅도 provider 가 필요해진다.
 */
let sessionUid: string | null = null;

export function hatcheryCacheUid(): string | null {
  return sessionUid;
}

export function startHatcheryDatabaseSession(session: AuthSession): void {
  sessionUid = session.uid;
  hatcheryDatabase.init();
}

/**
 * 세션이 끝날 때 데이터베이스를 폐기한다.
 *
 * 브라우저 종료일 때만 로컬 캐시를 남긴다. `AppSessionLifecycle` 이 재개용 로컬 상태에 쓰는
 * 규칙과 같다. 다음 경우에는 데이터베이스 전체를 지운다:
 * - 사람이 세션을 끝냈다(로그아웃).
 * - 세션이 바뀌었다(무효화·원격 로그아웃·사용자 교체).
 * 저장소는 origin 단위다. 지우지 않으면 공유 PC 에서 데이터가 다음 사용자의 디스크에 남는다.
 *
 * 먼저 **동기로** 연결을 닫고, 삭제는 그 뒤에 비동기로 실행한다. `dispose()` 는 `pagehide`
 * 에서도 호출되므로 기다릴 수 없다. 삭제를 먼저 시작하면 그 완료 콜백이 이미 시작된 다음
 * 세션을 닫는다.
 *
 * 남는 경우: 사용자를 바꾼 직후 새 세션이 삭제보다 먼저 데이터베이스를 열면 삭제가 막혀
 * 이전 바이트가 남는다. 그 경우에도 레코드 키에 uid 가 있어 다음 사용자는 읽지 못한다.
 * 남은 데이터는 다음 로그아웃에 제거된다.
 */
export function endHatcheryDatabaseSession(reason: AppSessionEndReason): void {
  sessionUid = null;
  hatcheryDatabase.dispose();
  if (reason === "browserExit" || reason === "appUnmount") {
    return;
  }
  void hatcheryDatabase.clearAll();
}
